<template>
  <expansion-panel
    uid="expansion-panel-vehicle-data"
    :title="t('dealer-bike.registration.vehicle-data-title')"
    :readonly="false"
    :hideDivider="true">
    <template v-slot:panel-content>
      <m-form-row :label="t('dealer-bike.registration.lbl-vin')" v-if="!reduced">
        <p v-if="descriptionList && dealerBikeVehicleVin">{{ dealerBikeVehicleVin }}</p>

        <m-text-field
          v-else
          data-cy="dealerBikeVehicleVin"
          v-model="dealerBikeVehicleVin"
          :placeholder="t('dealer-bike.registration.lbl-vin-placeholder')"
          :rules="readonly ? [] : vinRules"
          :maxlength="NUM_CHARACTERS_VALID_VIN"
          :readonly="readonly || isDealerBikeMapitLoading"
          :errorMessages="vinAvailableError"
          :successMessages="vinAvailableMigration"
          :loading="vinValidationLoader"
          :error="vinAvailableError || vinValidationLoader" />
      </m-form-row>

      <m-form-row :label="t('dealer-bike.registration.lbl-model')">
        <p v-if="descriptionList && selectedModelName">{{ selectedModelName }}</p>

        <m-text-field
          v-else-if="showLabels"
          data-cy="dealerBikeVehicleModelName"
          :placeholder="t('dealer-bike.registration.lbl-model-placeholder')"
          :value="selectedModelName"
          :rules="readonly ? [] : requiredRules"
          :disabled="readonly" />

        <m-select
          v-else
          data-cy="dealerBikeVehicleModelName"
          :placeholder="t('dealer-bike.registration.lbl-model-placeholder')"
          v-model="selectedModel"
          itemText="description"
          :items="models"
          :rules="readonly ? [] : requiredRules"
          :disabled="readonly"
          :loading="isModelsLoading"
          return-object />
      </m-form-row>

      <m-form-row :label="t('dealer-bike.registration.lbl-description')">
        <p v-if="descriptionList && selectedMaterialName">{{ selectedMaterialName }}</p>

        <m-text-field
          v-else-if="showLabels"
          data-cy="dealerBikeVehicleModelName"
          :placeholder="t('dealer-bike.registration.lbl-model-placeholder')"
          :value="selectedMaterialName"
          :rules="readonly ? [] : requiredRules"
          :disabled="readonly" />

        <m-select
          v-else
          data-cy="dealerBikeVehicleModelSubname"
          :placeholder="t('dealer-bike.registration.lbl-description-placeholder')"
          v-model="selectedMaterial"
          itemText="description"
          :items="materials"
          :rules="readonly ? [] : requiredRules"
          :disabled="!selectedModel || readonly || isMaterialsLoading"
          :loading="isMaterialsLoading"
          return-object />
      </m-form-row>

      <m-form-row :label="t('dealer-bike.registration.lbl-plate')">
        <m-registration-number-field
          data-cy="dealerBikeVehicleRegistrationNumber"
          v-model="dealerBikeVehicleRegistrationNumber"
          :rules="plateRules"
          :readonly="readonly || isMigration"
          :country="country" />
      </m-form-row>

      <m-form-row :label="t('dealer-bike.registration.lbl-registration-date')" v-if="!reduced">
        <m-date-picker
          data-cy="dealerBikeRegistrationDate"
          v-model="dealerBikeRegistrationDate"
          :disabled="!!readonly"
          :placeholder="t('dealer-bike.registration.lbl-registration-date-placeholder')"
          :max="moment().format('YYYY-MM-DD')"
          :max-error-text="t('dealer-bike.registration.invalid-date')"
          :min-error-text="t('dealer-bike.registration.min-error-text')"
          min="2021-01-01"
          :hideDetails="showValidations ? false : hideDetails['setDealerBikeRegistrationDate']"
          :locale="locale" />
      </m-form-row>

      <m-form-row :label="t('dealer-bike.registration.lbl-license')">
        <m-select
          data-cy="dealerBikeLicense"
          v-model="dealerBikeLicense"
          :placeholder="t('dealer-bike.registration.lbl-license-placeholder')"
          :items="licenseList"
          :rules="readonly ? [] : requiredRules"
          :readonly="readonly" />
      </m-form-row>

      <m-form-row :label="t('dealer-bike.registration.lbl-usage-type')" v-if="!reduced">
        <mapit-input
          uid="dealerBikeUsageType"
          :value="dealerBikeUsageType"
          align="left"
          :type="InputTypes.RADIO_BUTTONS"
          :listItems="usageTypeList"
          @changeValue="handleUsageTypeChange"
          :returnObject="true"
          :rules="readonly ? [] : requiredRules"
          :hideDetails="showValidations ? false : hideDetails['setDealerBikeUsageType']"
          :disabled="readonly" />
      </m-form-row>
    </template>
  </expansion-panel>
</template>

<script lang="ts">
import {computed, defineComponent, Ref, ref, watch} from '@vue/composition-api'
import {head} from 'lodash'
import moment from 'moment'

import dealerBikesApi from '@/api/dealerBikes.api'
import {NUM_CHARACTERS_VALID_VIN, status, vinValidation} from '@/api/vehicle.api.js'
import ExpansionPanel from '@/components/general/ExpansionPanel.vue'
import MapitInput from '@/components/general/MapitInput.vue'
import {useMaterials, useModels} from '@/composables'
import {isAlphanumeric, isEmpty, isRequired, lengthEqualTo} from '@/helpers/validations'
import {useI18n} from '@/i18n'
import {MaterialWithDescription, ModelWithDescription} from '@/types'
import {DealerBikeStatus, UsageType} from '@/types/dealer-bikes'
import {DrivingLicense} from '@/types/dynamodb-types'
import {InputTypes} from '@/utils/constants'
import {mapGetters} from '@/utils/map-store'
import {
  SET_DEALER_BIKE_LICENSE,
  SET_DEALER_BIKE_MAPIT,
  SET_DEALER_BIKE_MODEL_ID,
  SET_DEALER_BIKE_MODEL_NAME,
  SET_DEALER_BIKE_MODEL_SUBNAME,
  SET_DEALER_BIKE_REGISTRATION_DATE,
  SET_DEALER_BIKE_REGISTRATION_NUMBER,
  SET_DEALER_BIKE_VIN,
  SET_IS_MIGRATION_DEALER_BIKE,
  SET_IS_VALID_DEALER_BIKE_MAPIT,
  SET_MIGRATION_DEMO_BIKE_DATA,
} from '@/vuex/mutation-types'

export default defineComponent({
  name: 'VehicleData',
  components: {ExpansionPanel, MapitInput},
  props: {
    showValidations: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    reduced: {
      type: Boolean,
      default: false,
    },
    showLabels: {
      type: Boolean,
      default: false,
    },
    descriptionList: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, {root}) {
    const {t} = useI18n()

    const hideDetails = ref({
      setDealerBikeRegistrationDate: true,
      setDealerBikeUsageType: true,
      setDealerBikeVehicleRegistrationNumber: undefined as boolean | undefined,
    })

    const selectedModel = ref() as Ref<ModelWithDescription | undefined>
    const selectedMaterial = ref() as Ref<MaterialWithDescription | undefined>

    const vin = ref<string | undefined>(undefined)
    const vinAvailableError = ref('')
    const vinAvailableMigration = ref('')
    const vinValidationLoader = ref(false)
    const vinLoading = ref(false)
    const abortController = ref<AbortController>(new AbortController())

    const requiredRules = ref([isRequired])
    const vinRules = ref([
      (value: string) => isEmpty(value, 'honda.registration.vehicle.validations.vin_empty'),
      (value: string) => isAlphanumeric(value, 'honda.registration.vehicle.validations.vin_incorrect_format'),
      (value: string) => lengthEqualTo(value, 17, 'honda.validation.fields.vin'),
    ])
    const plateRules = ref([
      isEmpty,
      (value: string) =>
        isAlphanumeric(value, 'honda.registration.vehicle.validations.registration_number_incorrect_format'),
    ])

    const usageTypeList = computed(() => {
      return [UsageType.Demo, UsageType.Courtesy].map(item => {
        return {text: t(`dealer-bike.registration.lbl-usage-type-${item.toLowerCase()}`), value: item}
      })
    })

    const licenseList = computed(() => {
      return [DrivingLicense.A1, DrivingLicense.A2, DrivingLicense.A].map(item => ({
        text: item === DrivingLicense.A1 ? 'A1/B' : item,
        value: item,
      }))
    })

    const {
      isMigrationDealerBike: isMigration,
      getDealerBikeUsageType: dealerBikeUsageType,
      getDealerBikeSelected: dealerBikeSelected,
      getCountry: country,
      getLocale: locale,
      getBranch: branch,
      isDealerBikeMapitLoading,
      getDealerBikeRegistrationDate,
      getDealerBikeVehicleVin,
      getDealerBikeLicense,
      getDealerBikeVehicleRegistrationNumber,
      getDealerBikeVehicleModelName,
      getDealerBikeVehicleModelSubname,
    } = mapGetters(root.$store.getters)

    const {models, loading: isModelsLoading} = useModels({
      branch: branch.value,
      immediate: !props.showLabels,
    })

    const {materials, loading: isMaterialsLoading} = useMaterials({
      branch: branch.value,
      model: selectedModel,
    })

    const selectedModelName = computed(() => {
      return props.showLabels || props.descriptionList
        ? (selectedModel.value?.name ?? getDealerBikeVehicleModelName.value)
        : ''
    })

    const selectedMaterialName = computed(() => {
      return props.showLabels || props.descriptionList
        ? (selectedMaterial.value?.description ?? getDealerBikeVehicleModelSubname.value)
        : ''
    })

    const dealerBikeRegistrationDate = computed({
      get: () => getDealerBikeRegistrationDate.value,
      set: (value: string) => commit(SET_DEALER_BIKE_REGISTRATION_DATE, value),
    })

    const dealerBikeVehicleVin = computed({
      get: () => getDealerBikeVehicleVin.value,
      set: (value: string) => {
        if (vinLoading.value) {
          abortController.value.abort()
          abortController.value = new AbortController()
          vinLoading.value = false
        }
        validateVin(value.toUpperCase())
      },
    })

    const dealerBikeLicense = computed({
      get: () => getDealerBikeLicense.value,
      set: (value: string) => commit(SET_DEALER_BIKE_LICENSE, value),
    })

    const dealerBikeVehicleRegistrationNumber = computed({
      get: () => getDealerBikeVehicleRegistrationNumber.value,
      set: (value: string) =>
        commit(SET_DEALER_BIKE_REGISTRATION_NUMBER, value.toUpperCase().replace(/[^A-Z0-9]/g, '')),
    })

    watch(dealerBikeRegistrationDate, () => {
      hideDetails.value.setDealerBikeRegistrationDate = false
    })

    watch(selectedModel, value => {
      commit(SET_DEALER_BIKE_MODEL_NAME, value?.description)
    })

    watch(selectedMaterial, value => {
      commit(SET_DEALER_BIKE_MODEL_SUBNAME, value?.description)
    })

    watch(selectedMaterial, value => {
      commit(SET_DEALER_BIKE_MODEL_ID, value?.idMat)
    })

    watch(materials, (value, oldValue) => {
      if (!props.readonly && oldValue.length === 0 && value.length === 1) {
        selectedMaterial.value = value[0]
      }
    })

    async function loadDealerBikeByRegistrationNumber(registrationNumber: string, signal?: AbortSignal) {
      return dealerBikesApi
        .getDealerBikeByRegistrationNumber(registrationNumber, signal)
        .then(data => {
          commit(SET_IS_MIGRATION_DEALER_BIKE, !head(data.data.data))
          return data
        })
        .catch(e => {
          if (e.response.status === 404) {
            commit(SET_IS_MIGRATION_DEALER_BIKE, true)
            return null
          } else {
            commit(SET_IS_MIGRATION_DEALER_BIKE, false)
            console.error(e)
            throw e
          }
        })
    }

    function commit(mutation: string, payload: any) {
      root.$store.commit(mutation, payload)
    }

    async function updateVehicle(action: keyof typeof hideDetails.value, value: any) {
      await root.$store.dispatch(action, value)
      hideDetails.value[action] = false
    }

    function resetVinErrors() {
      vinAvailableMigration.value = ''
      vinAvailableError.value = ''
    }
    async function checkVinIsAvailable(vin: string) {
      const [maybeExDealerBike, result] = await Promise.all([
        dealerBikesApi
          .getDealerBikeByVin(vin, abortController.value.signal)
          .then(res => head(res.data.data.filter(db => db.status === DealerBikeStatus.Retired))),
        vinValidation(vin, abortController.value.signal),
      ])

      if (maybeExDealerBike) {
        vinAvailableError.value = t('honda.registration.vehicle.validations.vin_already_used_demo_bike').toString()
        return false
      } else if (result.key === status.ALREADY_USED_VIN.key && result.isDemoBike) {
        try {
          await loadDealerBikeByRegistrationNumber(result.registrationNumber, abortController.value.signal)
          hideDetails.value['setDealerBikeVehicleRegistrationNumber'] = false

          commit(SET_DEALER_BIKE_REGISTRATION_NUMBER, result.registrationNumber)
          commit(SET_DEALER_BIKE_MAPIT, {imei: result.deviceImei, id: result.deviceId})
          commit(SET_IS_VALID_DEALER_BIKE_MAPIT, true)
          commit(SET_MIGRATION_DEMO_BIKE_DATA, {registrationId: result.registrationId, vehicleId: result.vehicleId})

          resetVinErrors()
          isMigration.value
            ? (vinAvailableMigration.value = t(
                'honda.registration.vehicle.validations.vin_migrated_demo_bike',
              ).toString())
            : (vinAvailableError.value = t(
                'honda.registration.vehicle.validations.vin_already_used_demo_bike',
              ).toString())
        } catch (e) {
          resetVinErrors()
        }
        return
      }
      vinAvailableError.value = result.error ? t(result.status).toString() : ''
    }
    async function validateVin(vin: string) {
      vinLoading.value = true
      vinValidationLoader.value = true
      resetVinErrors()

      commit(SET_DEALER_BIKE_VIN, vin.toUpperCase())
      commit(SET_DEALER_BIKE_REGISTRATION_NUMBER, '')
      commit(SET_IS_MIGRATION_DEALER_BIKE, false)
      commit(SET_DEALER_BIKE_MAPIT, null)
      commit(SET_IS_VALID_DEALER_BIKE_MAPIT, false)
      root.$store.dispatch('imeiNeedsClearing', true)

      const validVin = vin.length === NUM_CHARACTERS_VALID_VIN && typeof isAlphanumeric(vin.toUpperCase()) !== 'string'
      if (validVin) await checkVinIsAvailable(vin.toUpperCase())
      vinValidationLoader.value = false
    }

    function handleUsageTypeChange(value: typeof usageTypeList.value) {
      updateVehicle('setDealerBikeUsageType', value)
    }

    return {
      t,
      moment,
      InputTypes,
      NUM_CHARACTERS_VALID_VIN,
      hideDetails,
      usageTypeList,
      licenseList,
      requiredRules,
      vin,
      vinAvailableError,
      vinAvailableMigration,
      vinValidationLoader,
      vinRules,
      vinLoading,
      plateRules,
      isMigration,
      dealerBikeUsageType,
      dealerBikeSelected,
      country,
      locale,
      isDealerBikeMapitLoading,
      dealerBikeRegistrationDate,
      dealerBikeVehicleVin,
      dealerBikeLicense,
      dealerBikeVehicleRegistrationNumber,
      models,
      materials,
      isModelsLoading,
      isMaterialsLoading,
      selectedModel,
      selectedMaterial,
      selectedModelName,
      selectedMaterialName,
      loadDealerBikeByRegistrationNumber,
      commit,
      updateVehicle,
      resetVinErrors,
      checkVinIsAvailable,
      validateVin,
      handleUsageTypeChange,
    }
  },
})
</script>
